"use client";

import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import useSWR from "swr";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  type DragEndEvent,
  type DragStartEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fetchView, createView, updateView, deleteView as apiDeleteView } from "@/lib/api/views";
import {
  createWorkspaceBlock,
  createWorkspacePage,
  deleteWorkspacePage,
  updateWorkspacePage,
  type WorkspacePageSummary,
} from "@/lib/api/workspace-pages";
import {
  fetchSidebarItems,
  reorderSidebarItems as apiReorderSidebarItems,
  setSidebarItemFavorite as apiSetSidebarItemFavorite,
} from "@/lib/api/sidebar-items";
import type { SidebarItem } from "@/lib/sidebar-items";
import { LEGACY_HOME_PATH } from "@/lib/app-routes";
import { fetchTasks, type Task } from "@/lib/api/tasks";
import { apiFetch } from "@/lib/api/client";
import { cancelApiWarmups, isApiWarmupRecentlyDone, startApiWarmup } from "@/lib/api/browser-warmup";
import { useMarketData } from "@/app/context/DataContext";
import { useStore } from "@/app/context/StoreContext";
import { withDataScope } from "@/app/context/DataScopeContext";
import { usePromotions } from "@/hooks/usePromotions";
import { useRefresh } from "@/app/context/RefreshContext";
import { PanelLeftClose, PanelLeftOpen, UserRound } from "lucide-react";
import { useExitAnimation } from "@/app/hooks/useExitAnimation";
import { duplicateWorkspacePage as duplicatePageApi } from "@/app/workspace/duplicate-page";
import ViewIcon, { LUCIDE_ICONS } from "./ViewIcon";
import StoreSwitcher from "./StoreSwitcher";
import TemplatesModal from "./templates/TemplatesModal";
import dynamic from "next/dynamic";
import { undoableDelete } from "./undoable-delete";
import { showToast } from "./Toast";
import type { SidebarInitialViews, SidebarView } from "@/lib/sidebar-views";
import { getDataDemandEndpoints } from "@/lib/data-demand-endpoints";
import { getInitialViewDataNeeds } from "@/lib/view-data-needs";
import { preloadColumnLabelsClient } from "@/lib/use-column-labels";
import { sidebarItemHref } from "@/app/flow-test/sidebar-links";
// LEGACY: старый стандартный редактор таблицы. Оставлен в проекте как запасной,
// но основной поток создания и редактирования таблиц теперь открывает
// ViewEditorV3Variant2.
// const ViewBuilder = dynamic(() => import("./ViewBuilder"), { ssr: false });
const ViewEditorV3Variant2 = dynamic(() => import("./ViewEditorV3Variant2"), { ssr: false });
import * as storage from "@/lib/storage";

// LEGACY: ключ старого localStorage-избранного. Используется только в
// одноразовой миграции (см. effect в Sidebar) — после переноса на сервер
// очищается, новые отметки сразу пишутся в БД через PATCH /api/views.
const LEGACY_FAVORITES_KEY = "frogstat_favorite_views";
const LEGACY_FAVORITES_MIGRATED_KEY = "frogstat_favorites_migrated_v1";

const TASKS_LAST_SEEN_KEY = "frogstat_tasks_last_seen_at";
const VIEWS_CACHE_KEY_PREFIX = "frogstat_sidebar_views_cache_v1";
const VIEWS_LAST_ACCOUNT_KEY = "frogstat_sidebar_views_last_account_v1";
const VIEWS_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const ICON_PICKER_WIDTH = 256;
const ICON_PICKER_MAX_HEIGHT = 256;
const VIEW_MENU_WIDTH = 160;
const VIEW_MENU_ESTIMATED_HEIGHT = 190;
const CREATE_MENU_WIDTH = 196;
const CREATE_MENU_ESTIMATED_HEIGHT = 132;
const POPOVER_GAP = 4;
const VIEWPORT_PADDING = 8;
const EMPTY_CLUSTER_MAP: Record<string, string> = {};
const SIDEBAR_METADATA_DELAY_MS = 2_000;
const HEAVY_VIEW_SIDEBAR_METADATA_DELAY_MS = 30_000;

function sidebarViewScopeQuery(
  view: Pick<View, "storeScope" | "selectedStoreIds">,
  current: { mode: "single" | "all"; storeId: string | null },
) {
  const storeScope = view.storeScope ?? "current";
  if (storeScope === "all") return "?stores=all";
  if (storeScope === "selected") {
    const ids = [...new Set(view.selectedStoreIds ?? [])];
    return ids.length ? `?stores=${encodeURIComponent(ids.join(","))}` : "";
  }
  if (current.mode === "all") return "?stores=all";
  return current.storeId ? `?stores=${encodeURIComponent(current.storeId)}` : "";
}

function viewsCacheKey(accountId: string) {
  return `${VIEWS_CACHE_KEY_PREFIX}:${accountId}`;
}

type View = SidebarView;
type DragGroup = "fav" | "rest";
type ViewMenuAnchor = {
  top: number;
  bottom: number;
  right: number;
  placement: "above" | "below";
};

function insertSidebarItemAfter(items: SidebarItem[], sourceItemId: string, item: SidebarItem): SidebarItem[] {
  const withoutItem = items.filter(current => current.id !== item.id);
  const sourceIndex = withoutItem.findIndex(current => current.id === sourceItemId);
  if (sourceIndex < 0) {
    return [item, ...withoutItem].map((current, index) => ({ ...current, position: index }));
  }
  const next = [...withoutItem];
  next.splice(sourceIndex + 1, 0, item);
  return next.map((current, index) => ({ ...current, position: index }));
}

function SortableSidebarView({
  id,
  active,
  menuOpen,
  disabled = false,
  children,
}: {
  id: string;
  active: boolean;
  menuOpen: boolean;
  disabled?: boolean;
  children: (args: {
    isDragging: boolean;
    dragAttributes: ReturnType<typeof useSortable>["attributes"];
    dragListeners: ReturnType<typeof useSortable>["listeners"];
  }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      className="touch-none"
      style={{
        position: "relative",
        zIndex: menuOpen ? 80 : isDragging ? 60 : undefined,
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : undefined,
      }}
    >
      {children({ isDragging: isDragging || active, dragAttributes: attributes, dragListeners: listeners })}
    </div>
  );
}

function SidebarDragPreview({ item }: { item: SidebarItem }) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-lg px-3 py-[5px] text-sm font-medium shadow-[0_10px_26px_rgba(15,23,42,0.18)]"
      style={{
        width: 196,
        background: "var(--sidebar-hover)",
        color: "var(--sidebar-text-active)",
      }}
    >
      <ViewIcon icon={item.icon ?? (item.kind === "page" ? "lucide:FileText" : "lucide:Table")} size={15} />
      <span className="truncate">{item.title}</span>
    </div>
  );
}

function TaskBadge({ count, compact = false }: { count: number; compact?: boolean }) {
  if (count <= 0) return null;
  const label = count > 99 ? "99+" : String(count);
  return (
    <span
      className={`${compact ? "absolute -right-1 -top-1 min-w-[16px] px-1 text-[9px]" : "ml-auto min-w-[18px] px-1.5 text-[10px]"} inline-flex h-[16px] items-center justify-center rounded-full bg-[#E3DFD3] font-bold leading-none text-[#666666]`}
      aria-label={`Новых задач: ${label}`}
    >
      {label}
    </span>
  );
}

export default function Sidebar({
  collapsed,
  onToggle,
  initialViews = null,
  restoreCollapsedImmediately = false,
}: {
  collapsed: boolean;
  onToggle: () => void;
  initialViews?: SidebarInitialViews;
  restoreCollapsedImmediately?: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const { lastJobAt } = useRefresh();
  const { stores, store: activeStore, mode: activeStoreMode, account: activeAccount, loading: storeLoading, user } = useStore();
  const isAdminUser = user?.role === "admin";
  const activeAccountId = activeAccount?.id ?? null;
  const { clusterMap, wbRegions } = useMarketData();
  const [views, setViews] = useState<View[]>(() => initialViews?.views ?? []);
  const [viewsAccountId, setViewsAccountId] = useState<string | null>(() => initialViews?.accountId ?? null);
  const [workspacePages, setWorkspacePages] = useState<WorkspacePageSummary[]>(() => initialViews?.workspacePages ?? []);
  const [workspacePagesAccountId, setWorkspacePagesAccountId] = useState<string | null>(() => initialViews?.accountId ?? null);
  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>(() => initialViews?.items ?? []);
  const [sidebarItemsAccountId, setSidebarItemsAccountId] = useState<string | null>(() => initialViews?.accountId ?? null);
  const [legacyViewIdByPageId, setLegacyViewIdByPageId] = useState<Record<string, string>>(
    () => initialViews?.legacyViewIdByPageId ?? {},
  );
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [createMenuPos, setCreateMenuPos] = useState<{ top: number; left: number }>({ top: 56, left: 8 });
  const [tasksLastSeenAt, setTasksLastSeenAt] = useState<number | null>(null);
  const [sidebarMetadataReady, setSidebarMetadataReady] = useState(false);
  const [expandedContentVisible, setExpandedContentVisible] = useState(!collapsed);
  const restoredCompactAppliedRef = useRef(false);
  const [compactTip, setCompactTip] = useState<{ label: string; top: number } | null>(null);
  const createButtonRef = useRef<HTMLButtonElement | null>(null);
  const sidebarNavigationSeqRef = useRef(0);

  useEffect(() => {
    if (pathname === "/tasks") {
      setSidebarMetadataReady(true);
      return;
    }
    setSidebarMetadataReady(false);
    const delay = pathname.startsWith("/view/") || pathname.startsWith("/templates/preview/")
      ? HEAVY_VIEW_SIDEBAR_METADATA_DELAY_MS
      : SIDEBAR_METADATA_DELAY_MS;
    const timer = window.setTimeout(() => setSidebarMetadataReady(true), delay);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  useLayoutEffect(() => {
    if (!collapsed) {
      setExpandedContentVisible(true);
      return;
    }
    if (restoreCollapsedImmediately && !restoredCompactAppliedRef.current) {
      restoredCompactAppliedRef.current = true;
      setExpandedContentVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setExpandedContentVisible(false), 90);
    return () => window.clearTimeout(timer);
  }, [collapsed, restoreCollapsedImmediately]);

  // Переключение избранного: оптимистично обновляем UI и параллельно
  // сохраняем на сервер. Если запрос упал — откатываем локальное состояние.
  const toggleFavorite = useCallback(async (item: SidebarItem) => {
    const newValue = !item.isFavorite;
    setSidebarItems(prev => prev.map(current => current.id === item.id ? { ...current, isFavorite: newValue } : current));
    if (item.kind === "table") {
      setViews(prev => prev.map(v => v.id === item.targetId ? { ...v, isFavorite: newValue } : v));
    }
    try {
      await apiSetSidebarItemFavorite(item.id, newValue);
    } catch {
      setSidebarItems(prev => prev.map(current => current.id === item.id ? { ...current, isFavorite: !newValue } : current));
      if (item.kind === "table") {
        setViews(prev => prev.map(v => v.id === item.targetId ? { ...v, isFavorite: !newValue } : v));
      }
    }
  }, []);

  // Контекстное меню
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [renderedMenuId, setRenderedMenuId] = useState<string | null>(null);
  const [viewMenuPos, setViewMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [viewMenuAnchor, setViewMenuAnchor] = useState<ViewMenuAnchor | null>(null);
  const viewMenuPanelRef = useRef<HTMLDivElement | null>(null);
  const viewMenu = useExitAnimation(openMenuId !== null);
  useEffect(() => {
    if (openMenuId) setRenderedMenuId(openMenuId);
    else if (!viewMenu.mounted) setRenderedMenuId(null);
  }, [openMenuId, viewMenu.mounted]);
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!renderedMenuId || !viewMenu.mounted || !viewMenuPanelRef.current || !viewMenuAnchor) return;
    const rect = viewMenuPanelRef.current.getBoundingClientRect();
    const minTop = VIEWPORT_PADDING;
    const maxTop = Math.max(VIEWPORT_PADDING, window.innerHeight - rect.height - VIEWPORT_PADDING);
    const top = Math.min(
      maxTop,
      Math.max(
        minTop,
        viewMenuAnchor.placement === "below"
          ? viewMenuAnchor.bottom + POPOVER_GAP
          : viewMenuAnchor.top - POPOVER_GAP - rect.height,
      ),
    );
    const maxLeft = Math.max(VIEWPORT_PADDING, window.innerWidth - rect.width - VIEWPORT_PADDING);
    const left = Math.min(maxLeft, Math.max(VIEWPORT_PADDING, viewMenuAnchor.right - rect.width));
    if (Math.abs(top - viewMenuPos.top) > 0.5 || Math.abs(left - viewMenuPos.left) > 0.5) {
      setViewMenuPos({ top, left });
    }
  }, [renderedMenuId, viewMenu.mounted, viewMenuAnchor, viewMenuPos.left, viewMenuPos.top]);

  const toggleViewMenu = useCallback((id: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (openMenuId === id) {
      setOpenMenuId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const hasRoomBelow = rect.bottom + POPOVER_GAP + VIEW_MENU_ESTIMATED_HEIGHT <= window.innerHeight - VIEWPORT_PADDING;
    const placement = hasRoomBelow ? "below" : "above";
    const top = hasRoomBelow
      ? rect.bottom + POPOVER_GAP
      : Math.max(VIEWPORT_PADDING, rect.top - POPOVER_GAP - VIEW_MENU_ESTIMATED_HEIGHT);
    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(rect.right - VIEW_MENU_WIDTH, window.innerWidth - VIEW_MENU_WIDTH - VIEWPORT_PADDING),
    );
    setViewMenuAnchor({ top: rect.top, bottom: rect.bottom, right: rect.right, placement });
    setViewMenuPos({ top, left });
    setOpenMenuId(id);
  }, [openMenuId]);

  const [iconPickerId, setIconPickerId] = useState<string | null>(null);
  const [iconPickerPos, setIconPickerPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  useEffect(() => {
    if (!iconPickerId) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-icon-picker]")) setIconPickerId(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [iconPickerId]);

  const openIconPicker = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (iconPickerId === id) {
      setIconPickerId(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const hasRoomBelow = rect.bottom + POPOVER_GAP + ICON_PICKER_MAX_HEIGHT <= window.innerHeight - VIEWPORT_PADDING;
    const top = hasRoomBelow
      ? rect.bottom + POPOVER_GAP
      : Math.max(VIEWPORT_PADDING, rect.top - POPOVER_GAP - ICON_PICKER_MAX_HEIGHT);
    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(rect.left, window.innerWidth - ICON_PICKER_WIDTH - VIEWPORT_PADDING),
    );
    setIconPickerPos({ top, left });
    setIconPickerId(id);
  };

  const saveIcon = async (id: string, icon: string) => {
    const item = sidebarItems.find(current => current.id === id);
    if (!item) return;
    const nextIcon = icon || null;
    setSidebarItems(prev => prev.map(current => current.id === id ? { ...current, icon: nextIcon } : current));
    if (item.kind === "table") {
      setViews(prev => prev.map(v => v.id === item.targetId ? { ...v, icon: icon || undefined } : v));
    } else {
      setWorkspacePages(prev => prev.map(page => page.id === item.targetId ? { ...page, icon: nextIcon } : page));
    }
    setIconPickerId(null);
    if (item.kind === "table") {
      await updateView(item.targetId, icon ? { icon } : { icon: undefined });
    } else {
      await updateWorkspacePage(item.targetId, { icon: nextIcon });
    }
  };

  const tasksSwr = useSWR<Task[]>(sidebarMetadataReady ? "/api/tasks" : null, fetchTasks, {
    revalidateOnFocus: true,
    refreshInterval: 60_000,
  });
  const tasks = useMemo(() => tasksSwr.data ?? [], [tasksSwr.data]);
  const newestTaskCreatedAt = useMemo(() => {
    let newest = 0;
    for (const task of tasks) {
      const time = Date.parse(task.created_at);
      if (Number.isFinite(time) && time > newest) newest = time;
    }
    return newest;
  }, [tasks]);
  const newTasksCount = useMemo(() => {
    if (pathname === "/tasks" || !tasksLastSeenAt) return 0;
    return tasks.reduce((count, task) => {
      const time = Date.parse(task.created_at);
      return Number.isFinite(time) && time > tasksLastSeenAt ? count + 1 : count;
    }, 0);
  }, [pathname, tasks, tasksLastSeenAt]);

  useEffect(() => {
    setTasksLastSeenAt(storage.get<number | null>(TASKS_LAST_SEEN_KEY, null));
  }, []);

  useEffect(() => {
    if (tasksLastSeenAt !== null || tasks.length === 0) return;
    const initialSeenAt = Date.now();
    storage.set(TASKS_LAST_SEEN_KEY, initialSeenAt);
    setTasksLastSeenAt(initialSeenAt);
  }, [tasks.length, tasksLastSeenAt]);

  useEffect(() => {
    if (pathname !== "/tasks") return;
    const seenAt = Math.max(Date.now(), newestTaskCreatedAt);
    storage.set(TASKS_LAST_SEEN_KEY, seenAt);
    setTasksLastSeenAt(seenAt);
  }, [newestTaskCreatedAt, pathname]);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [activeDrag, setActiveDrag] = useState<{ id: string; group: DragGroup } | null>(null);
  const sidebarDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const suppressClickAfterDragRef = useRef(false);
  const sidebarLinkPaddingPointerRef = useRef<{ itemId: string; x: number; y: number } | null>(null);
  const sidebarBlankDragRef = useRef(false);
  const sidebarItemsRequestSeqRef = useRef(0);
  const emptyViewsRetryRef = useRef<number | null>(null);
  const acceptNextEmptyViewsRef = useRef(false);
  const skipInitialViewsRefreshRef = useRef<string | null>(initialViews?.accountId ?? null);
  const activeViewsCacheKey = activeAccountId ? viewsCacheKey(activeAccountId) : null;
  const viewsAreTemporary = !!viewsAccountId && viewsAccountId !== activeAccountId;
  const workspacePagesAreTemporary = !!workspacePagesAccountId && workspacePagesAccountId !== activeAccountId;
  const sidebarItemsAreTemporary = !!sidebarItemsAccountId && sidebarItemsAccountId !== activeAccountId;

  useEffect(() => {
    if (activeAccountId) return;
    const lastAccountId = storage.get<string | null>(VIEWS_LAST_ACCOUNT_KEY, null);
    if (!lastAccountId) return;
    const cached = storage.getCached<View[]>(viewsCacheKey(lastAccountId), VIEWS_CACHE_MAX_AGE_MS);
    if (!cached || !Array.isArray(cached)) return;
    setViews(cached);
    setViewsAccountId(lastAccountId);
  }, [activeAccountId]);

  useEffect(() => {
    if (!activeViewsCacheKey || !activeAccountId) return;
    storage.set(VIEWS_LAST_ACCOUNT_KEY, activeAccountId);
    if (initialViews?.accountId === activeAccountId) {
      setViews(initialViews.views);
      setViewsAccountId(activeAccountId);
      setWorkspacePages(initialViews.workspacePages);
      setWorkspacePagesAccountId(activeAccountId);
      setSidebarItems(initialViews.items);
      setLegacyViewIdByPageId(initialViews.legacyViewIdByPageId);
      setSidebarItemsAccountId(activeAccountId);
      storage.setCached(activeViewsCacheKey, initialViews.views);
      return;
    }
    const cached = storage.getCached<View[]>(activeViewsCacheKey, VIEWS_CACHE_MAX_AGE_MS);
    setViews(cached && Array.isArray(cached) ? cached : []);
    setViewsAccountId(activeAccountId);
  }, [activeAccountId, activeViewsCacheKey, initialViews]);

  useEffect(() => {
    if (storeLoading || activeAccountId) return;
    setViews([]);
    setViewsAccountId(null);
    setWorkspacePages([]);
    setWorkspacePagesAccountId(null);
    setSidebarItems([]);
    setLegacyViewIdByPageId({});
    setSidebarItemsAccountId(null);
  }, [activeAccountId, storeLoading]);

  const refreshSidebarData = useCallback(() => {
    if (!activeAccountId) {
      setViews([]);
      setViewsAccountId(null);
      setWorkspacePages([]);
      setWorkspacePagesAccountId(null);
      setSidebarItems([]);
      setLegacyViewIdByPageId({});
      setSidebarItemsAccountId(null);
      return;
    }
    const requestAccountId = activeAccountId;
    const requestSeq = ++sidebarItemsRequestSeqRef.current;
    fetchSidebarItems().then(data => {
      if (requestSeq !== sidebarItemsRequestSeqRef.current) return;
      const list = data.views;
      setViewsAccountId(requestAccountId);
      setWorkspacePagesAccountId(requestAccountId);
      setSidebarItemsAccountId(requestAccountId);
      setViews(prev => {
        if (prev.length > 0 && list.length === 0 && !acceptNextEmptyViewsRef.current) {
          acceptNextEmptyViewsRef.current = true;
          if (emptyViewsRetryRef.current) window.clearTimeout(emptyViewsRetryRef.current);
          emptyViewsRetryRef.current = window.setTimeout(() => {
            emptyViewsRetryRef.current = null;
            window.dispatchEvent(new Event("frogstat:view-updated"));
          }, 350);
          return prev;
        }
        acceptNextEmptyViewsRef.current = false;
        storage.setCached(viewsCacheKey(requestAccountId), list);
        storage.set(VIEWS_LAST_ACCOUNT_KEY, requestAccountId);
        return list;
      });
      setWorkspacePages(data.workspacePages);
      setSidebarItems(data.items);
      setLegacyViewIdByPageId(data.legacyViewIdByPageId);
    }).catch(() => {});
  }, [activeAccountId]);

  useEffect(() => {
    if (skipInitialViewsRefreshRef.current === activeAccountId) {
      skipInitialViewsRefreshRef.current = null;
    } else {
      refreshSidebarData();
    }
  }, [activeAccountId, refreshSidebarData]);
  useEffect(() => () => {
    if (emptyViewsRetryRef.current) window.clearTimeout(emptyViewsRetryRef.current);
  }, []);

  // Слушаем глобальное событие обновления видов — в ТЕКУЩЕЙ вкладке.
  // Диспатчится из редактора вида, TemplateCard.handleApply и т.д.
  useEffect(() => {
    const viewHandler = () => refreshSidebarData();
    const workspaceHandler = () => refreshSidebarData();
    const storeHandler = () => refreshSidebarData();
    window.addEventListener("frogstat:view-updated", viewHandler);
    window.addEventListener("frogstat:workspace-pages-updated", workspaceHandler);
    // После переключения магазина/аккаунта виды могли поменяться — перезагружаем.
    // StoreContext диспатчит frogstat:store-switched после successful switch.
    window.addEventListener("frogstat:store-switched", storeHandler);
    return () => {
      window.removeEventListener("frogstat:view-updated", viewHandler);
      window.removeEventListener("frogstat:workspace-pages-updated", workspaceHandler);
      window.removeEventListener("frogstat:store-switched", storeHandler);
    };
  }, [refreshSidebarData]);

  // Cross-tab сигнал через storage event — когда пользователь применил
  // шаблон в ДРУГОЙ вкладке (например в preview-вкладке), эта вкладка
  // об этом узнаёт через изменение `frogstat_views_ping` в localStorage.
  // Browser автоматически фаерит storage event на всех вкладках того же
  // origin при изменении localStorage из скрипта в другой вкладке.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "frogstat_views_ping") refreshSidebarData();
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [refreshSidebarData]);

  // Одноразовая миграция: переносим избранное из localStorage на сервер.
  // До 2026-04-10 виды-избранные жили только в localStorage и терялись при
  // смене браузера/инкогнито. После миграции они хранятся в JSON-поле data
  // у views. Эффект:
  //   1. Читает старый localStorage-ключ.
  //   2. Для каждого ID, который есть в загруженных видах, шлёт PATCH с
  //      isFavorite=true.
  //   3. Очищает старый ключ и ставит флажок «уже мигрировано», чтобы
  //      больше не повторять.
  useEffect(() => {
    if (views.length === 0) return; // ждём загрузки видов
    if (storage.get<boolean>(LEGACY_FAVORITES_MIGRATED_KEY, false)) return;

    const legacyIds = storage.get<string[]>(LEGACY_FAVORITES_KEY, []);
    if (!Array.isArray(legacyIds) || legacyIds.length === 0) {
      // Нечего мигрировать, но всё равно ставим флажок, чтобы не проверять снова.
      storage.set(LEGACY_FAVORITES_MIGRATED_KEY, true);
      return;
    }

    const existingIds = new Set(views.map(v => v.id));
    const toMigrate = legacyIds.filter(id => existingIds.has(id));

    Promise.all(
      toMigrate.map(id => updateView(id, { isFavorite: true }).catch(() => null))
    ).then(() => {
      storage.remove(LEGACY_FAVORITES_KEY);
      storage.set(LEGACY_FAVORITES_MIGRATED_KEY, true);
      refreshSidebarData();
    });
  }, [views, refreshSidebarData]);

  // Отменяемое удаление (Gmail-паттерн вместо confirm): страница исчезает из
  // сайдбара сразу, реальный DELETE — через 5с; «Отменить» просто перечитывает
  // список с сервера (вид там ещё жив). С открытой страницы уводим сразу.
  const deleteView = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (pathname === `/view/${id}`) router.push("/");
    setSidebarItems(prev => prev.filter(item => !(item.kind === "table" && item.targetId === id)));
    undoableDelete({
      message: "Страница удалена",
      commit: async () => { await apiDeleteView(id); refreshSidebarData(); },
      restore: () => refreshSidebarData(),
      errorMessage: "Не удалось удалить страницу",
    });
  };

  const duplicateView = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const config = await fetchView(id);
      const existingNames = views.map(v => v.name);
      const base = config.name.replace(/\s*\(\d+\)$/, "");
      let n = 2;
      while (existingNames.includes(`${base} (${n})`)) n++;
      // Удаляем поля которые сервер ставит сам (id, accountId, createdByUserId,
      // createdAt, updatedAt, shareToken, sharedStoreId) — иначе сервер мог бы
      // их учесть/игнорировать неконсистентно. Явный destructure безопаснее
      // чем надеяться что сервер их проигнорирует.
      const {
        name: _omitName, columns: _omitCols, id: _omitId,
        accountId: _omitAcc, createdByUserId: _omitCBy,
        createdAt: _omitCA, updatedAt: _omitUA,
        shareToken: _omitST, sharedStoreId: _omitSSid,
        ...rest
      } = config;
      void _omitName; void _omitCols; void _omitId; void _omitAcc; void _omitCBy;
      void _omitCA; void _omitUA; void _omitST; void _omitSSid;
      const created = await createView(`${base} (${n})`, config.columns, rest, id);
      setViews(prev => {
        const withoutCreated = prev.filter(view => view.id !== created.id);
        const sourceIndex = withoutCreated.findIndex(view => view.id === id);
        if (sourceIndex === -1) return [created, ...withoutCreated];
        const next = [...withoutCreated];
        next.splice(sourceIndex + 1, 0, created);
        return next;
      });
      setSidebarItems(prev => insertSidebarItemAfter(prev, `table:${id}`, {
        id: `table:${created.id}`,
        kind: "table",
        targetId: created.id,
        title: created.name,
        icon: created.icon ?? null,
        isFavorite: created.isFavorite === true,
        position: 0,
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      }));
      refreshSidebarData();
      // Сообщаем всем подписчикам (ViewPageContent и пр.) что список видов
      // изменился — они перечитают.
      window.dispatchEvent(new Event("frogstat:view-updated"));
    } catch (err) {
      console.error("[duplicateView] failed:", err);
      showToast("Не удалось дублировать страницу.", "error");
    }
  };

  // Сохранить текущий вид как личный шаблон («Мои шаблоны»). Создаётся запись
  // в templates с owner_user_id = user.id — будет видна только этому юзеру
  // в галерее и доступна в любом его аккаунте.
  const saveAsMyTemplate = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const config = await fetchView(id);
    const res = await fetch("/api/templates", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: config.name,
        icon: config.icon,
        description: "",
        category: "",
        scope: "user",
        config,
      }),
    });
    if (res.ok) {
      showToast(`Шаблон «${config.name}» сохранён в Мои шаблоны.`);
    } else {
      const d = await res.json().catch(() => ({}));
      showToast(`Не удалось сохранить: ${d.error ?? res.status}`, "error");
    }
  };

  const duplicateWorkspacePage = async (pageId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const created = await duplicatePageApi(pageId);
      if (activeAccountId) setWorkspacePagesAccountId(activeAccountId);
      setWorkspacePages(prev => {
        const withoutCreated = prev.filter(page => page.id !== created.id);
        const sourceIndex = withoutCreated.findIndex(page => page.id === pageId);
        if (sourceIndex < 0) return [created, ...withoutCreated];
        const next = [...withoutCreated];
        next.splice(sourceIndex + 1, 0, created);
        return next;
      });
      setSidebarItems(prev => {
        const sourceItem = prev.find(item => item.id === `page:${pageId}`);
        return insertSidebarItemAfter(prev, `page:${pageId}`, {
          id: `page:${created.id}`,
          kind: "page",
          targetId: created.id,
          title: created.title,
          icon: created.icon,
          isFavorite: sourceItem?.isFavorite ?? false,
          position: 0,
          createdAt: created.created_at,
          updatedAt: created.updated_at,
        });
      });
      window.dispatchEvent(new Event("frogstat:workspace-pages-updated"));
      router.push(`/page/${created.id}`);
    } catch (err) {
      console.error("[duplicateWorkspacePage] failed:", err);
      showToast("Не удалось дублировать страницу", "error");
    }
  };

  // Отменяемое удаление, как у видов: из сайдбара сразу, DELETE через 5с.
  const deleteWorkspacePageFromSidebar = (pageId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const page = workspacePages.find(item => item.id === pageId);
    if (pathname === `/page/${pageId}`) router.push("/");
    setWorkspacePages(prev => prev.filter(item => item.id !== pageId));
    setSidebarItems(prev => prev.filter(item => !(item.kind === "page" && item.targetId === pageId)));
    undoableDelete({
      message: page ? `Страница «${page.title}» удалена` : "Страница удалена",
      commit: async () => {
        await deleteWorkspacePage(pageId);
        window.dispatchEvent(new Event("frogstat:workspace-pages-updated"));
      },
      restore: () => {
        if (page) setWorkspacePages(prev => prev.some(p => p.id === pageId) ? prev : [...prev, page]);
        refreshSidebarData();
      },
      errorMessage: "Не удалось удалить страницу",
    });
  };

  const openTableCreate = useCallback(() => {
    setEditId(null);
    setBuilderOpen(true);
  }, []);

  const positionCreateMenu = useCallback((anchor?: HTMLElement | null) => {
    const rect = anchor?.getBoundingClientRect() ?? createButtonRef.current?.getBoundingClientRect();
    const top = rect
      ? Math.min(window.innerHeight - CREATE_MENU_ESTIMATED_HEIGHT - VIEWPORT_PADDING, rect.bottom + POPOVER_GAP)
      : 56;
    const left = rect
      ? Math.min(window.innerWidth - CREATE_MENU_WIDTH - VIEWPORT_PADDING, Math.max(VIEWPORT_PADDING, rect.left))
      : 54;
    setCreateMenuPos({ top: Math.max(VIEWPORT_PADDING, top), left });
  }, []);

  const openCreateMenu = useCallback((event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    const anchor = event?.currentTarget ?? createButtonRef.current;
    if (createMenuOpen) {
      setCreateMenuOpen(false);
      return;
    }
    positionCreateMenu(anchor);
    setCreateMenuOpen(true);
  }, [createMenuOpen, positionCreateMenu]);

  const createWorkspacePageItem = useCallback(async () => {
    setCreateMenuOpen(false);
    try {
      const page = await createWorkspacePage("Новая страница");
      if (activeAccountId) setWorkspacePagesAccountId(activeAccountId);
      setWorkspacePages(prev => [...prev.filter(item => item.id !== page.id), page]);
      setSidebarItems(prev => [{
        id: `page:${page.id}`,
        kind: "page",
        targetId: page.id,
        title: page.title,
        icon: page.icon,
        isFavorite: false,
        position: Math.min(0, ...prev.map(item => item.position)) - 1,
        createdAt: page.created_at,
        updatedAt: page.updated_at,
      }, ...prev.filter(item => item.id !== `page:${page.id}`)]);
      const href = `/page/${page.id}`;
      router.push(href);
      window.setTimeout(() => {
        window.dispatchEvent(new Event("frogstat:workspace-pages-updated"));
      }, 0);
    } catch {
      showToast("Не удалось создать страницу", "error");
    }
  }, [activeAccountId, router]);

  const openTableCreateFromMenu = useCallback(() => {
    setCreateMenuOpen(false);
    openTableCreate();
  }, [openTableCreate]);

  const openTemplatesFromMenu = useCallback(() => {
    setCreateMenuOpen(false);
    setTemplatesModalOpen(true);
  }, []);

  const handleEditorV2Created = useCallback((view: View) => {
    setBuilderOpen(false);
    const createdAt = view.createdAt ?? new Date().toISOString();
    const updatedAt = view.updatedAt ?? createdAt;
    if (activeAccountId) {
      setViewsAccountId(activeAccountId);
      setSidebarItemsAccountId(activeAccountId);
    }
    setViews(prev => {
      const withoutView = prev.filter(item => item.id !== view.id);
      if (editId) {
        const index = prev.findIndex(item => item.id === view.id);
        if (index < 0) return [view, ...withoutView];
        const next = [...prev];
        next[index] = view;
        return next;
      }
      return [view, ...withoutView];
    });
    setSidebarItems(prev => {
      const id = `table:${view.id}`;
      const existing = prev.find(item => item.id === id);
      const item: SidebarItem = {
        id,
        kind: "table",
        targetId: view.id,
        title: view.name,
        icon: view.icon ?? null,
        isFavorite: existing?.isFavorite ?? view.isFavorite === true,
        position: existing?.position ?? Math.min(0, ...prev.map(current => current.position)) - 1,
        createdAt: existing?.createdAt ?? createdAt,
        updatedAt,
      };
      const withoutItem = prev.filter(current => current.id !== id);
      if (editId && existing) {
        return prev.map(current => current.id === id ? item : current);
      }
      return [item, ...withoutItem];
    });
    refreshSidebarData();
    window.dispatchEvent(new Event("frogstat:view-updated"));
    router.push(`/view/${view.id}`);
  }, [activeAccountId, editId, refreshSidebarData, router]);
  const openEdit = (id: string, e: React.MouseEvent) => { e.preventDefault(); setEditId(id); setBuilderOpen(true); };

  // Глобальное событие "frogstat:new-view" — топбар (quick-action кнопка)
  // просит открыть единое меню создания. Чтобы не тянуть Sidebar state наружу,
  // общаемся через CustomEvent. Тот же паттерн используется для
  // "frogstat:view-updated" (см. строку 147 ниже).
  useEffect(() => {
    const handler = () => openCreateMenu();
    window.addEventListener("frogstat:new-view", handler);
    return () => window.removeEventListener("frogstat:new-view", handler);
  }, [openCreateMenu]);

  useEffect(() => {
    if (!createMenuOpen) return;
    const closeByClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest("[data-sidebar-create-menu]") || target.closest("[data-sidebar-create-button]")) return;
      setCreateMenuOpen(false);
    };
    const closeByWindowChange = () => setCreateMenuOpen(false);
    document.addEventListener("mousedown", closeByClick);
    window.addEventListener("resize", closeByWindowChange);
    return () => {
      document.removeEventListener("mousedown", closeByClick);
      window.removeEventListener("resize", closeByWindowChange);
    };
  }, [createMenuOpen]);

  const sidebarClustersSwr = useSWR<Record<string, string>>(
    builderOpen ? "/api/clusters" : null,
    url => apiFetch<Record<string, string>>(url),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const sidebarClusterMap = sidebarClustersSwr.data && typeof sidebarClustersSwr.data === "object"
    ? sidebarClustersSwr.data
    : EMPTY_CLUSTER_MAP;
  const availableClusters = useMemo(
    () => [...new Set([...Object.values(clusterMap), ...Object.values(sidebarClusterMap)])].filter(Boolean).sort(),
    [clusterMap, sidebarClusterMap],
  );
  const { promoData, promoLoading } = usePromotions(lastJobAt, builderOpen);

  const favItems = useMemo(() => sidebarItems.filter(item => item.isFavorite), [sidebarItems]);
  const mainSidebarItems = useMemo(() => sidebarItems.filter(item => !item.isFavorite), [sidebarItems]);
  const favOrderedIds = useMemo(() => favItems.map(item => item.id), [favItems]);
  const mainSidebarItemIds = useMemo(() => mainSidebarItems.map(item => item.id), [mainSidebarItems]);
  const existingViewNames = useMemo(() => views.map(v => v.name), [views]);
  const renderedMenuItem = useMemo(() => renderedMenuId ? sidebarItems.find(item => item.id === renderedMenuId) ?? null : null, [renderedMenuId, sidebarItems]);
  const renderedMenuView = useMemo(
    () => renderedMenuItem?.kind === "table" ? views.find(v => v.id === renderedMenuItem.targetId) ?? null : null,
    [renderedMenuItem, views],
  );
  const activeDragItem = useMemo(
    () => activeDrag ? sidebarItems.find(item => item.id === activeDrag.id) ?? null : null,
    [activeDrag, sidebarItems],
  );
  const compactMode = collapsed && !expandedContentVisible;
  const useLegacyViewLinks = pathname === LEGACY_HOME_PATH || pathname.startsWith("/view/");
  const sidebarTextStyle = {
    display: "inline-block",
    opacity: compactMode ? 0 : 1,
    maxWidth: compactMode ? 0 : 160,
    overflow: "hidden",
    whiteSpace: "nowrap" as const,
    transition: "opacity var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1), max-width var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1)",
  };
  const sidebarSectionLabelStyle = {
    opacity: compactMode ? 0 : 1,
    transition: "opacity var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1)",
  };
  const compactItemClass = "mx-2 h-8 w-8 rounded-lg";
  const expandedItemClass = "mx-2 rounded-lg";
  const sidebarItemShellClass = compactMode ? compactItemClass : expandedItemClass;
  const compactSectionClass = "mx-2 mt-2 mb-1 flex h-8 w-8 items-center justify-center rounded-lg";
  const showCompactTip = useCallback((label: string, element: HTMLElement) => {
    if (!compactMode) return;
    const rect = element.getBoundingClientRect();
    setCompactTip({ label, top: rect.top + rect.height / 2 });
  }, [compactMode]);
  const hideCompactTip = useCallback(() => setCompactTip(null), []);

  useEffect(() => {
    if (!compactMode) setCompactTip(null);
  }, [compactMode]);

  const resetSortableDragState = useCallback(() => {
    setActiveDrag(null);
    setDraggingId(null);
    window.setTimeout(() => {
      suppressClickAfterDragRef.current = false;
    }, 0);
  }, []);

  const preloadViewNavigationData = useCallback((view: View | null) => {
    if (!view) return;
    const scopeQuery = sidebarViewScopeQuery(view, {
      mode: activeStoreMode,
      storeId: activeStore?.id ?? null,
    });
    const keys = new Set<string>([withDataScope("/api/products/lite", scopeQuery)]);
    for (const source of getInitialViewDataNeeds(view)) {
      if (source === "promos") {
        keys.add(withDataScope(`/api/promotions?viewId=${encodeURIComponent(view.id)}&mode=initial`, scopeQuery));
        continue;
      }
      for (const endpoint of getDataDemandEndpoints(source)) keys.add(withDataScope(endpoint, scopeQuery));
    }

    preloadColumnLabelsClient();
    for (const key of keys) {
      if (isApiWarmupRecentlyDone(key)) continue;
      startApiWarmup(key, "sidebar");
    }
  }, [activeStore?.id, activeStoreMode]);

  const cancelSidebarNavigationWork = useCallback(() => {
    cancelApiWarmups("sidebar");
  }, []);

  const notifyRoutePending = useCallback((href: string, view?: View, title?: string) => {
    if (typeof window !== "undefined") {
      const target = new URL(href, window.location.href);
      const current = `${window.location.pathname}${window.location.search}`;
      const next = `${target.pathname}${target.search}`;
      if (next !== current) {
        document.documentElement.dataset.frogstatPendingPathname = target.pathname;
        window.dispatchEvent(new CustomEvent("frogstat:route-pending", {
          detail: {
            view,
            pathname: target.pathname,
            title: title ?? view?.name,
            kind: target.pathname.startsWith("/page/") ? "page" : target.pathname.startsWith("/view/") ? "table" : "route",
          },
        }));
      }
    }
  }, []);

  const startSidebarNavigation = useCallback((href: string, view: View | null, item: SidebarItem) => {
    sidebarNavigationSeqRef.current += 1;
    const seq = sidebarNavigationSeqRef.current;
    cancelSidebarNavigationWork();
    if (typeof window !== "undefined" && view) {
      notifyRoutePending(href, view ?? undefined, item.title);
      preloadViewNavigationData(view);
      if (sidebarNavigationSeqRef.current !== seq) return;
      router.push(href);
      return;
    }
    notifyRoutePending(href, view ?? undefined, item.title);
    router.push(href);
  }, [cancelSidebarNavigationWork, notifyRoutePending, preloadViewNavigationData, router]);

  const startStaticSidebarNavigation = useCallback((href: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.currentTarget.getAttribute("target");
    if (target && target !== "_self") return;
    event.preventDefault();
    sidebarNavigationSeqRef.current += 1;
    cancelSidebarNavigationWork();
    notifyRoutePending(href);
    router.push(href);
  }, [cancelSidebarNavigationWork, notifyRoutePending, router]);

  useEffect(() => () => {
    cancelSidebarNavigationWork();
  }, [cancelSidebarNavigationWork]);

  const handleSortableDragStart = useCallback((group: DragGroup, event: DragStartEvent) => {
    const id = String(event.active.id);
    suppressClickAfterDragRef.current = true;
    setOpenMenuId(null);
    setActiveDrag({ id, group });
    setDraggingId(id);
  }, []);

  useEffect(() => {
    if (!activeDrag) return;
    const resetAfterDnd = () => window.setTimeout(resetSortableDragState, 0);
    const resetWhenHidden = () => {
      if (document.visibilityState === "hidden") resetSortableDragState();
    };
    window.addEventListener("pointerup", resetAfterDnd, true);
    window.addEventListener("mouseup", resetAfterDnd, true);
    window.addEventListener("touchend", resetAfterDnd, true);
    window.addEventListener("blur", resetSortableDragState);
    document.addEventListener("visibilitychange", resetWhenHidden);
    return () => {
      window.removeEventListener("pointerup", resetAfterDnd, true);
      window.removeEventListener("mouseup", resetAfterDnd, true);
      window.removeEventListener("touchend", resetAfterDnd, true);
      window.removeEventListener("blur", resetSortableDragState);
      document.removeEventListener("visibilitychange", resetWhenHidden);
    };
  }, [activeDrag, resetSortableDragState]);

  const handleSortableDragEnd = useCallback((groupIds: string[], event: DragEndEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : "";
    if (!overId || activeId === overId) {
      resetSortableDragState();
      return;
    }

    const oldIndex = groupIds.indexOf(activeId);
    const newIndex = groupIds.indexOf(overId);
    if (oldIndex < 0 || newIndex < 0) {
      resetSortableDragState();
      return;
    }

    const next = arrayMove(groupIds, oldIndex, newIndex);
    setSidebarItems(prev => {
      const order = new Map(next.map((id, index) => [id, index]));
      return [...prev].sort((a, b) => {
        const aOrder = order.get(a.id);
        const bOrder = order.get(b.id);
        if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
        if (aOrder !== undefined) return -1;
        if (bOrder !== undefined) return 1;
        return a.position - b.position;
      }).map((item, index) => ({ ...item, position: index }));
    });
    void apiReorderSidebarItems(next).catch(() => refreshSidebarData());
    resetSortableDragState();
  }, [refreshSidebarData, resetSortableDragState]);

  const renderSidebarItem = (item: SidebarItem) => {
    const legacyViewId = useLegacyViewLinks && item.kind === "page"
      ? legacyViewIdByPageId[item.targetId]
      : undefined;
    const effectiveViewId = item.kind === "table" ? item.targetId : legacyViewId;
    const view = effectiveViewId ? views.find(v => v.id === effectiveViewId) ?? null : null;
    const href = sidebarItemHref(item, legacyViewId);
    const isActive = pathname === href;
    const isDragging = draggingId === item.id;
    const menuVisibleForRow = renderedMenuId === item.id && viewMenu.mounted;
    const disabled = sidebarItemsAreTemporary || (effectiveViewId ? viewsAreTemporary : workspacePagesAreTemporary);
    const icon = item.icon ?? (effectiveViewId ? "lucide:Table" : "lucide:FileText");

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled || suppressClickAfterDragRef.current) {
        event.preventDefault();
        return;
      }
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.currentTarget.getAttribute("target");
      if (target && target !== "_self") return;
      event.preventDefault();
      startSidebarNavigation(href, view, item);
    };

    return (
      <SortableSidebarView
        key={item.id}
        id={item.id}
        active={activeDrag?.id === item.id}
        menuOpen={menuVisibleForRow}
        disabled={disabled}
      >
        {({ isDragging: sortableDragging, dragAttributes, dragListeners }) => (
          <div
            data-sidebar-item-row
            data-sidebar-view-row={effectiveViewId ? "" : undefined}
            data-sidebar-page-row={effectiveViewId ? undefined : ""}
            {...dragAttributes}
            {...dragListeners}
            aria-disabled={disabled}
            className={`group flex items-center transition-colors select-none ${disabled ? "cursor-default opacity-70" : "cursor-pointer"} ${sidebarItemShellClass} ${compactMode ? "justify-center" : ""} ${sortableDragging ? "opacity-30" : ""}`}
            style={{
              marginTop: compactMode ? 0 : 1,
              marginBottom: compactMode ? 0 : 1,
              ...(isActive && !compactMode ? { background: "var(--sidebar-hover)" } : {}),
            }}
            onMouseEnter={e => {
              showCompactTip(item.title, e.currentTarget);
              if (isActive || isDragging) return;
              if (compactMode) {
                const cell = e.currentTarget.querySelector<HTMLElement>("[data-sidebar-item-icon-cell]");
                if (cell) cell.style.background = "var(--sidebar-hover)";
                return;
              }
              (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)";
            }}
            onMouseLeave={e => {
              hideCompactTip();
              if (isActive) return;
              if (compactMode) {
                const cell = e.currentTarget.querySelector<HTMLElement>("[data-sidebar-item-icon-cell]");
                if (cell) cell.style.background = "";
                return;
              }
              (e.currentTarget as HTMLElement).style.background = "";
            }}
          >
            <Link
              href={href}
              prefetch={false}
              data-sidebar-compact-view-link
              data-sidebar-item-icon-cell
              className={`${compactMode ? "flex" : "hidden"} h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-medium`}
              style={{
                color: isActive ? "var(--sidebar-text-active)" : "var(--sidebar-text)",
                ...(isActive ? { background: "var(--sidebar-hover)" } : {}),
              }}
              aria-disabled={disabled}
              onClick={handleLinkClick}
              onAuxClick={e => {
                if (disabled || suppressClickAfterDragRef.current) e.preventDefault();
              }}
            >
              <ViewIcon icon={icon} size={15} />
            </Link>
            <div
              data-sidebar-expanded-view-cell
              data-sidebar-item-icon-cell
              className={`${compactMode ? "hidden" : "flex"} flex-1 min-w-0 items-stretch gap-1.5 truncate rounded-lg pl-3 text-sm font-medium`}
              style={{ color: isActive ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}
              onClick={e => {
                // Клик по «мёртвым» зонам строки (левый отступ, зазор у иконки)
                // тоже должен вести на страницу. Саму ссылку и кнопки (иконка/меню)
                // пропускаем — они обрабатывают клик сами, двойной навигации нет
                // (ссылка делает preventDefault, кнопки ловятся по closest).
                if (disabled || suppressClickAfterDragRef.current) return;
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                if ((e.target as HTMLElement).closest("a, button, [data-icon-picker]")) return;
                startSidebarNavigation(href, view, item);
              }}
            >
              <button
                type="button"
                data-icon-picker
                onClick={e => openIconPicker(item.id, e)}
                onPointerDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={`shrink-0 self-center flex items-center justify-center w-5 h-5 rounded transition-colors ${disabled ? "" : "cursor-pointer"}`}
                style={{ color: "var(--sidebar-text)" }}
                disabled={disabled}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = "var(--sidebar-icon-hover)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = "";
                }}
                aria-label={`Сменить иконку ${item.title}`}
              >
                <ViewIcon icon={icon} size={15} />
              </button>
              <Link
                href={href}
                prefetch={false}
                className="flex min-w-0 flex-1 items-center self-stretch py-[5px]"
                style={{ color: "inherit" }}
                aria-disabled={disabled}
                onPointerDownCapture={e => {
                  if (e.target !== e.currentTarget || e.button !== 0) return;
                  e.stopPropagation();
                }}
                onMouseDownCapture={e => {
                  if (e.target !== e.currentTarget || e.button !== 0) {
                    sidebarLinkPaddingPointerRef.current = null;
                    return;
                  }
                  sidebarLinkPaddingPointerRef.current = { itemId: item.id, x: e.clientX, y: e.clientY };
                  e.stopPropagation();
                }}
                onMouseUpCapture={e => {
                  const start = sidebarLinkPaddingPointerRef.current;
                  sidebarLinkPaddingPointerRef.current = null;
                  if (!start || start.itemId !== item.id || e.target !== e.currentTarget || disabled) return;
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
                  if (moved > 4) return;
                  e.preventDefault();
                  e.stopPropagation();
                  startSidebarNavigation(href, view, item);
                }}
                onClick={handleLinkClick}
                onAuxClick={e => {
                  if (disabled || suppressClickAfterDragRef.current) e.preventDefault();
                }}
              >
                <span className="min-w-0 flex-1 truncate" style={sidebarTextStyle}>
                  {item.title}
                </span>
              </Link>
            </div>
            {!disabled && (
              <div className="relative shrink-0" style={{ display: compactMode ? "none" : undefined }} onPointerDown={e => e.stopPropagation()} onMouseDown={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}>
                <button
                  onClick={e => toggleViewMenu(item.id, e)}
                  className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors mr-1 text-lg leading-none tracking-widest"
                  title="Действия"
                >
                  ···
                </button>
              </div>
            )}
          </div>
        )}
      </SortableSidebarView>
    );
  };

  const renderSortableItemGroup = (group: DragGroup, groupItems: SidebarItem[], groupIds: string[]) => (
    <DndContext
      id={`sidebar-${group}`}
      sensors={sidebarDragSensors}
      collisionDetection={closestCenter}
      onDragStart={event => handleSortableDragStart(group, event)}
      onDragCancel={resetSortableDragState}
      onDragEnd={event => handleSortableDragEnd(groupIds, event)}
    >
      <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
        {groupItems.map(renderSidebarItem)}
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeDrag?.group === group && activeDragItem ? <SidebarDragPreview item={activeDragItem} /> : null}
      </DragOverlay>
    </DndContext>
  );

  const renderMainSidebarItems = () => {
    if (mainSidebarItems.length === 0) return null;
    return renderSortableItemGroup("rest", mainSidebarItems, mainSidebarItemIds);
  };

  const sidebarShellStyle = {
    background: "var(--sidebar-bg)",
    borderRight: "1px solid var(--sidebar-border)",
    transition: "width var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1)",
    willChange: "width",
  };
  const sidebarShellClass = `fixed left-0 top-0 h-full ${collapsed ? "w-12" : "w-[220px]"} z-[25] flex flex-col overflow-hidden`;

  return (
    <div data-frogstat-sidebar-shell className={sidebarShellClass} style={sidebarShellStyle}>
      <div className="flex h-full w-full min-w-0 shrink-0 flex-col">
      <div className={`flex h-12 shrink-0 items-center ${compactMode ? "justify-center px-0" : "gap-2 px-2"}`} style={{ borderBottom: "1px solid var(--sidebar-border)" }}>
        {!compactMode && <span className="text-lg leading-none" style={sidebarTextStyle}>🐸</span>}
        {!compactMode && <span className="min-w-0 flex-1" />}
        <button onClick={onToggle} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors" style={{ color: "var(--sidebar-text-muted)" }} onMouseEnter={e => { showCompactTip(collapsed ? "Развернуть" : "Свернуть", e.currentTarget); (e.currentTarget as HTMLElement).style.color = "var(--sidebar-text-active)"; (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }} onMouseLeave={e => { hideCompactTip(); (e.currentTarget as HTMLElement).style.color = "var(--sidebar-text-muted)"; (e.currentTarget as HTMLElement).style.background = ""; }} title={compactMode ? undefined : collapsed ? "Развернуть" : "Свернуть"} aria-label={collapsed ? "Развернуть" : "Свернуть"}>
          {collapsed ? <PanelLeftOpen size={17} strokeWidth={1.8} /> : <PanelLeftClose size={17} strokeWidth={1.8} />}
        </button>
      </div>

      {/* Верхняя фиксированная часть: Главная / Создать / Финансы / Задачи.
          Не скроллится даже если видов много — всегда на месте. */}
      <div className="shrink-0 py-2">
        <div className={sidebarItemShellClass}
          style={pathname === LEGACY_HOME_PATH ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Сводка", e.currentTarget); if (pathname !== LEGACY_HOME_PATH) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (pathname !== LEGACY_HOME_PATH) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href={LEGACY_HOME_PATH}
            prefetch={false}
            onClick={e => startStaticSidebarNavigation(LEGACY_HOME_PATH, e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname === LEGACY_HOME_PATH ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Home" size={16} />
            <span style={sidebarTextStyle}>Сводка</span>
          </Link>
        </div>

        <div className={sidebarItemShellClass}
          style={createMenuOpen ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Создать", e.currentTarget); if (!createMenuOpen) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!createMenuOpen) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <button
            ref={createButtonRef}
            type="button"
            data-sidebar-create-button
            onClick={openCreateMenu}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium"
              : "flex w-full items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium"}
            style={{ color: createMenuOpen ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}
          >
            {/* кружок 20px в 16px-боксе: подпись «Создать» в одну линию с остальными (иконки 16px), кружок симметрично выступает по 2px */}
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: "var(--sidebar-new-btn)" }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
                  <line x1="5" y1="1" x2="5" y2="9"/><line x1="1" y1="5" x2="9" y2="5"/>
                </svg>
              </span>
            </span>
            <span style={sidebarTextStyle}>Создать</span>
          </button>
        </div>

        <div className={sidebarItemShellClass}
          style={pathname.startsWith("/neurostudio") ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Нейростудия beta", e.currentTarget); if (!pathname.startsWith("/neurostudio")) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!pathname.startsWith("/neurostudio")) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/neurostudio"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/neurostudio", e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname.startsWith("/neurostudio") ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Sparkles" size={16} />
            <span style={sidebarTextStyle}>Нейростудия</span>
            {!compactMode && (
              <span
                className="ml-auto rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                style={{ background: "var(--sidebar-hover)", color: "var(--sidebar-text-muted)" }}
              >
                beta
              </span>
            )}
          </Link>
        </div>

        <div className={sidebarItemShellClass}
          style={pathname.startsWith("/ads") ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Реклама", e.currentTarget); if (!pathname.startsWith("/ads")) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!pathname.startsWith("/ads")) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/ads"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/ads", e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname.startsWith("/ads") ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Megaphone" size={16} />
            <span style={sidebarTextStyle}>Реклама</span>
          </Link>
        </div>

        <div className={sidebarItemShellClass}
          style={pathname.startsWith("/event-feed") ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Лента событий", e.currentTarget); if (!pathname.startsWith("/event-feed")) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!pathname.startsWith("/event-feed")) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/event-feed"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/event-feed", e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname.startsWith("/event-feed") ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Calendar" size={16} />
            <span style={sidebarTextStyle}>Лента событий</span>
          </Link>
        </div>

        <div className={sidebarItemShellClass}
          style={pathname.startsWith("/ab-tests") ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("A/B тест beta", e.currentTarget); if (!pathname.startsWith("/ab-tests")) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!pathname.startsWith("/ab-tests")) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/ab-tests"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/ab-tests", e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname.startsWith("/ab-tests") ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:FlaskConical" size={16} />
            <span style={sidebarTextStyle}>A/B тест</span>
            {!compactMode && (
              <span
                className="ml-auto rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                style={{ background: "var(--sidebar-hover)", color: "var(--sidebar-text-muted)" }}
              >
                beta
              </span>
            )}
          </Link>
        </div>

        {/* Ассортиментная карта — СКРЫТА по просьбе (код сохранён; показать снова = false → true) */}
        {false && (
        <div className={sidebarItemShellClass}
          style={pathname.startsWith("/assortment-map") ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Ассортиментная карта beta", e.currentTarget); if (!pathname.startsWith("/assortment-map")) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!pathname.startsWith("/assortment-map")) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/assortment-map"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/assortment-map", e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname.startsWith("/assortment-map") ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Network" size={16} />
            <span style={sidebarTextStyle}>Ассортиментная карта</span>
            {!compactMode && (
              <span
                className="ml-auto rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                style={{ background: "var(--sidebar-hover)", color: "var(--sidebar-text-muted)" }}
              >
                beta
              </span>
            )}
          </Link>
        </div>
        )}


        {/* Поставки — ОСНОВНАЯ страница теперь /supply-pult (пульт). Прежняя /supply-planning доступна по прямой ссылке. */}
        <div className={sidebarItemShellClass}
          onMouseEnter={e => { showCompactTip("Поставки", e.currentTarget); (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); (e.currentTarget as HTMLElement).style.background = ""; }}>
          <a href="/supply-pult"
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Package" size={16} />
            <span style={sidebarTextStyle}>Поставки</span>
            {!compactMode && (
              <span
                className="ml-auto rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                style={{ background: "var(--sidebar-hover)", color: "var(--sidebar-text-muted)" }}
              >
                beta
              </span>
            )}
          </a>
        </div>

        {/* Финансы (beta) пока admin-only: API /api/admin/new-finance закрыт isAdmin,
            поэтому не-админу пункт нечего открывать — прячем, чтобы не вёл в ошибку. */}
        {isAdminUser && (
        <div className={sidebarItemShellClass}
          style={pathname.startsWith("/new-finance") ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Финансы beta", e.currentTarget); if (!pathname.startsWith("/new-finance")) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (!pathname.startsWith("/new-finance")) (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/new-finance"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/new-finance", e)}
            className={compactMode
              ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname.startsWith("/new-finance") ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Wallet" size={16} />
            <span style={sidebarTextStyle}>Финансы</span>
            {!compactMode && (
              <span
                className="ml-auto rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                style={{ background: "var(--sidebar-hover)", color: "var(--sidebar-text-muted)" }}
              >
                beta
              </span>
            )}
          </Link>
        </div>
        )}

        <div className={sidebarItemShellClass}
          style={pathname === "/tasks" ? { background: "var(--sidebar-hover)" } : undefined}
          onMouseEnter={e => { showCompactTip("Задачи", e.currentTarget); if (pathname !== "/tasks") (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
          onMouseLeave={e => { hideCompactTip(); if (pathname !== "/tasks") (e.currentTarget as HTMLElement).style.background = ""; }}>
          <Link href="/tasks"
            prefetch={false}
            onClick={e => startStaticSidebarNavigation("/tasks", e)}
            className={compactMode
              ? "relative flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors"
              : "relative flex items-center gap-2 rounded-lg py-[5px] pl-3 pr-2 text-sm font-medium transition-colors"}
            style={{ color: pathname === "/tasks" ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}>
            <ViewIcon icon="lucide:Layers" size={16} />
            <span style={sidebarTextStyle}>Задачи</span>
            <TaskBadge count={newTasksCount} compact={compactMode} />
          </Link>
        </div>

      </div>

      {/* Разделитель между фиксированной шапкой и скроллируемым списком видов.
          Фиксирован, всегда виден. Показывается только если есть хотя бы
          один вид — если видов нет, разделитель не нужен. */}
      {(favItems.length > 0 || mainSidebarItems.length > 0) && (
        <div className="shrink-0 h-px mx-0" style={{ background: "var(--sidebar-border)" }} />
      )}

      {/* Нижняя скроллируемая часть: Закрепленное + страницы/таблицы. Именно эта
          секция скроллится когда видов становится много — не весь сайдбар. */}
      <div
        data-sidebar-views-scroll
        className={`flex-1 min-h-0 overflow-y-auto pb-2 ${compactMode ? "sidebar-scroll-hidden" : ""}`}
        style={{ overscrollBehavior: "contain", background: "var(--sidebar-bg)", scrollbarWidth: compactMode ? "none" : undefined }}
        onPointerDownCapture={e => {
          const target = e.target as HTMLElement;
          if (target.closest("[data-sidebar-item-row],button,a,input,textarea,select,[data-icon-picker]")) return;
          sidebarBlankDragRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerMoveCapture={e => {
          if (!sidebarBlankDragRef.current) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerUpCapture={e => {
          if (!sidebarBlankDragRef.current) return;
          sidebarBlankDragRef.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerCancelCapture={() => {
          sidebarBlankDragRef.current = false;
        }}
      >
        {favItems.length > 0 && (
          <>
            <div
              data-sidebar-section-label
              className={compactMode
                ? compactSectionClass
                : "mt-2 mb-1 pl-5 text-[11px] tracking-wide flex items-center gap-1"}
              style={{ color: "var(--sidebar-label-color)", ...(compactMode ? {} : sidebarSectionLabelStyle) }}
              onMouseEnter={e => showCompactTip("Закрепленное", e.currentTarget)}
              onMouseLeave={hideCompactTip}
            >
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" x2="12" y1="17" y2="22" />
                <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
              </svg>
              <span data-sidebar-section-expanded className={compactMode ? "hidden" : ""}>Закрепленное</span>
            </div>
            {renderSortableItemGroup("fav", favItems, favOrderedIds)}
          </>
        )}

        {mainSidebarItems.length > 0 && (
          <>
            <div
              data-sidebar-section-label
              className={compactMode
                ? compactSectionClass
                : "mt-2 mb-1 pl-5 text-[11px] tracking-wide"}
              style={{ color: "var(--sidebar-label-color)", ...(compactMode ? {} : sidebarSectionLabelStyle) }}
              onMouseEnter={e => showCompactTip("Страницы", e.currentTarget)}
              onMouseLeave={hideCompactTip}
            >
              <span data-sidebar-section-compact className={compactMode ? "flex" : "hidden"}>
                <UserRound size={14} strokeWidth={1.8} />
              </span>
              <span data-sidebar-section-expanded className={compactMode ? "hidden" : ""}>Страницы</span>
            </div>
            {renderMainSidebarItems()}
          </>
        )}
      </div>

      {/* Переключатель магазинов — внизу, на месте бывшей кнопки «Настройки». */}
      <div className="shrink-0" style={{ borderTop: "1px solid var(--sidebar-border)" }}>
        <StoreSwitcher compact={compactMode} />
      </div>

      {createMenuOpen && typeof document !== "undefined" && createPortal(
        <div
          data-sidebar-create-menu
          className="ui-popover-panel ui-dropdown-panel fixed z-[9999] w-[196px] p-1"
          style={{ top: createMenuPos.top, left: createMenuPos.left }}
          onMouseDown={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
        >
          <button
            type="button"
            onClick={createWorkspacePageItem}
            className="ui-dropdown-item"
          >
            <ViewIcon icon="lucide:FileText" size={14} />
            Страница
          </button>
          <button
            type="button"
            onClick={openTableCreateFromMenu}
            className="ui-dropdown-item"
          >
            <ViewIcon icon="lucide:Table" size={14} />
            Таблица
          </button>
          <div className="ui-dropdown-divider" />
          <button
            type="button"
            onClick={openTemplatesFromMenu}
            className="ui-dropdown-item"
          >
            <ViewIcon icon="lucide:LayoutTemplate" size={14} />
            Готовые таблицы
          </button>
        </div>,
        document.body,
      )}

      {compactTip && compactMode && typeof document !== "undefined" && createPortal(
        <div
          className="pointer-events-none fixed left-[54px] z-[9999] whitespace-nowrap rounded-md border bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-lg"
          style={{ top: compactTip.top, transform: "translateY(-50%)", borderColor: "var(--sidebar-border)" }}
        >
          {compactTip.label}
        </div>,
        document.body,
      )}

      {builderOpen && (
        <ViewEditorV3Variant2
          open={builderOpen}
          viewId={editId}
          onClose={() => setBuilderOpen(false)}
          onCreated={handleEditorV2Created}
          onSaved={handleEditorV2Created}
          existingNames={existingViewNames}
          availableClusters={availableClusters}
          availableRegions={wbRegions}
          availablePromotions={promoData.actions}
          promoLoading={promoLoading}
          stores={stores}
          activeStoreId={activeStore?.id ?? null}
          activeStoreMode={activeStoreMode}
        />
      )}

      <TemplatesModal open={templatesModalOpen} onClose={() => setTemplatesModalOpen(false)} title="Готовые таблицы" />

      {renderedMenuItem && viewMenu.mounted && typeof document !== "undefined" && createPortal(
        <div
          ref={viewMenuPanelRef}
          data-sidebar-item-menu-panel
          className="ui-popover-panel ui-dropdown-panel fixed z-[9999] w-40"
          data-state={viewMenu.state}
          style={{ top: viewMenuPos.top, left: viewMenuPos.left }}
          onMouseDown={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
        >
          <button
            onClick={() => { void toggleFavorite(renderedMenuItem); setOpenMenuId(null); }}
            className="ui-dropdown-item">
            <svg className="w-3 h-3 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" x2="12" y1="17" y2="22" />
              <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
            </svg>
            {renderedMenuItem.isFavorite ? "Открепить" : "Закрепить"}
          </button>
          <button
            onClick={e => {
              if (renderedMenuItem.kind === "table") void duplicateView(renderedMenuItem.targetId, e);
              else void duplicateWorkspacePage(renderedMenuItem.targetId, e);
              setOpenMenuId(null);
            }}
            className="ui-dropdown-item">
            <svg className="w-3 h-3 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            Дублировать
          </button>
          {renderedMenuItem.kind === "table" && renderedMenuView ? (
            <>
              <button
                onClick={e => { openEdit(renderedMenuItem.targetId, e); setOpenMenuId(null); }}
                className="ui-dropdown-item">
                <span className="w-3 text-center text-gray-400">✎</span>
                Редактировать
              </button>
              <button
                onClick={e => { saveAsMyTemplate(renderedMenuItem.targetId, e); setOpenMenuId(null); }}
                className="ui-dropdown-item">
                <span className="w-3 text-center text-gray-400">⭐</span>
                В мои шаблоны
              </button>
            </>
          ) : null}
          <div className="ui-dropdown-divider" />
          <button
            onClick={e => {
              if (renderedMenuItem.kind === "table") void deleteView(renderedMenuItem.targetId, e);
              else void deleteWorkspacePageFromSidebar(renderedMenuItem.targetId, e);
              setOpenMenuId(null);
            }}
            className="ui-dropdown-item text-red-500 hover:bg-red-50">
            <span className="w-3 text-center">×</span>
            Удалить
          </button>
        </div>,
        document.body,
      )}

      {/* Мини-пикер иконки */}
      {iconPickerId && typeof document !== "undefined" && createPortal(
        <div data-icon-picker
          className="fixed z-[9999] rounded-xl shadow-xl p-2 w-64 max-h-64 overflow-y-auto ui-popover-panel"
          style={{ background: "var(--sidebar-bg)", border: "1px solid var(--sidebar-border)", top: iconPickerPos.top, left: iconPickerPos.left }}>
          <div className="grid grid-cols-8 gap-0.5 mb-2">
            {LUCIDE_ICONS.map(({ name, component: Comp }) => {
              const val = `lucide:${name}`;
              const current = sidebarItems.find(item => item.id === iconPickerId)?.icon;
              return (
                <button key={name} type="button"
                  onClick={() => saveIcon(iconPickerId, val)}
                  title={name}
                  className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${current === val ? "ring-1 ring-blue-400" : ""}`}
                  style={{ background: current === val ? "rgba(96,165,250,0.15)" : undefined, color: "var(--sidebar-text)" }}
                  onMouseEnter={e => { if (current !== val) (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
                  onMouseLeave={e => { if (current !== val) (e.currentTarget as HTMLElement).style.background = ""; }}>
                  <Comp size={13} />
                </button>
              );
            })}
          </div>
          <div className="h-px mb-2" style={{ background: "var(--sidebar-border)" }} />
          <div className="grid grid-cols-8 gap-0.5">
            {["📊","📈","📉","🛍️","🏪","📦","💰","🎯","🔥","⭐","🏆","📋","🗂️","🔍","💡","🚀","👕","👗","👟","🎒","🧴","🧸","💼","📌","🔖","📱","🖥️","🌟","⚡","💎","🎨","🌿","🎪","🐸","🏠","✅","📅","🌈","🧩","🎁","🎀","🎵","🎬","🏋️","🧠","🌍","🛒","🏷️","🧾","💳","🏦","🪙","📣","📢","🔔","🗓️","⏰","🕐","📍","🚩","🏁","🥇","🥈","🥉","🎖️","🏅","🎗️","🎲","🧮","🔢","💹","📲","🖨️","🗃️","🗄️","📁","📂","🗑️","✏️","🖊️","📝","📄","📃","📑","🗒️","🗞️","📰","📓","📔","📒","📕","📗","📘","📙","🔑","🔐","🔒","🛡️","⚙️","🔧","🔨","🛠️","🧲","💊","🩺","🌱","🌾","🍀","🌻","🌸","🍁","❄️","🌊","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪"].map(e => {
              const current = sidebarItems.find(item => item.id === iconPickerId)?.icon;
              return (
                <button key={e} type="button"
                  onClick={() => saveIcon(iconPickerId, e)}
                  className={`w-6 h-6 flex items-center justify-center rounded text-base transition-colors ${current === e ? "ring-1 ring-blue-400" : ""}`}
                  style={{ background: current === e ? "rgba(96,165,250,0.15)" : undefined }}
                  onMouseEnter={ev => { if (current !== e) (ev.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; }}
                  onMouseLeave={ev => { if (current !== e) (ev.currentTarget as HTMLElement).style.background = ""; }}>
                  {e}
                </button>
              );
            })}
          </div>
          {sidebarItems.find(item => item.id === iconPickerId)?.icon && (
            <button type="button"
              onClick={() => saveIcon(iconPickerId, "")}
              className="mt-2 w-full text-xs text-center py-1 rounded transition-colors"
              style={{ color: "var(--sidebar-text-muted)" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "var(--sidebar-hover)"; (e.currentTarget as HTMLElement).style.color = "var(--sidebar-text)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ""; (e.currentTarget as HTMLElement).style.color = "var(--sidebar-text-muted)"; }}>
              Убрать иконку
            </button>
          )}
        </div>,
        document.body,
      )}
      </div>
    </div>
  );
}
